import { configureRouter } from "@/.shared/services/routing-services";
import { configureDocumentViewer } from "@helpers/document-viewer-helper";
import { useAuthentication } from "@/.shared/services/auth";
import type { ExtendedUserInfo } from "@/.shared/services/auth";
import { useAuthorization } from "@/.shared/services/auth/authorization/authorization-service-impl";
import { PermissionsApp } from "@/.shared/services/auth/authorization/authorization-service-impl";
import { tienePermisoGestionOcasCatalogo } from "@/components/oca/shared/oca-auth";
import {
  esUsuarioPds,
  puedeVerNavActuaciones,
  puedeVerNavCrearActuacion,
  puedeVerCampanas,
  puedeVerNavDashboardWo,
  puedeVerNavDashboardKpiGmao,
  puedeVerNavEquipoMedida,
  puedeAccederRutasInspeccionesVisuales,
  puedeVerNavInspeccionesVisuales,
  puedeVerNavMaestroPagos,
  puedeVerNavMaestroPagosB2c,
  puedeVerNavMaestroPagosInspeccionesVisuales,
  puedeVerNavOcas,
  puedeVerNavTicketing,
  resolveRutaInicioPds,
  puedeVerNavBuscadorPrestadores,
  puedeVerNavSeguimientoOcas,
} from "@/helpers/pds-modulos-helper";
import { useApi } from "@/.shared/.api/integration";
import {
  isGestorRevenueAdmin,
  REVENUE_DASHBOARD_PATH,
  REVENUE_TOTAL_DEVENGADO_PATH,
} from "@/.shared/helpers/revenue-auth-helper";
import { puedeVerDashboardActivos } from "@helpers/activos-permissions-helper";

export function configureAppRoutes() {
  const puedeGestionarInteroperabilidad = () => {
    const autho = useAuthorization();
    return autho.hasPermission(PermissionsApp.EditAllTransacciones)
      || autho.hasPermission(PermissionsApp.GestionAvanzadaActuacionesAll);
  };
  configureRouter((router) => {
    const imageViewerRouteName = "image-viewer-view";
    router.addRoute(imageViewerRouteName, (b) => {
      b.withPath("visor-imagen")
        .withComponent(() => import("@views/ImageViewerView.vue"))
        .withAuthorization({})
        .withLayout("div");
    });
    configureDocumentViewer(imageViewerRouteName);

    router.addRoute("home", (b) =>
      b
        .withPath("")
        .withComponent(() => import("@views/PublicHomeView.vue"))
        .allowAnonymous()
        .configure((r) => {
          r.beforeEnter = async (to, from, next) => {
            if (useAuthentication().isLoggedIn()) {
              return next({ name: "private-home" });
            }

            next();
          };
        })
    );

    router.addRoute("private-home", (d) => {
      d.withPath("home")
        .withComponent(() => import("@/views/AuthorizedHomeView.vue"))
        .withAuthorization({})
        .configure((r) => {
          // r.redirect = "/clientes";
          r.beforeEnter = (to, from, next) => {
            const autho = useAuthorization();
            const userInfo = useAuthentication().userInfo as ExtendedUserInfo | null;

            if (esUsuarioPds(autho)) {
              return next(resolveRutaInicioPds(userInfo) ?? "/clientes");
            }

            const esUsuarioLoveCharger = autho.hasPermission(PermissionsApp.UserLoveCharger);
            const esUsuarioRsSync = autho.hasPermission(PermissionsApp.UserRssync);
            const soloTicketing = autho.hasPermission(PermissionsApp.GestionTicketingAll)
              && !autho.hasPermission(PermissionsApp.GestionAvanzadaActuacionesAll);
            const tienePermisoWo = autho.hasPermission(PermissionsApp.EditAllWo)
              || autho.hasPermission(PermissionsApp.EditSelfWo);

            if (tienePermisoWo) {
              next("/dashboard-wo");
            } else if (isGestorRevenueAdmin(autho)) {
              next(REVENUE_DASHBOARD_PATH);
            } else if (esUsuarioRsSync) {
              next("/informes");
            } else if (esUsuarioLoveCharger) {
              next("/gestion-actuaciones");
            } else if (soloTicketing) {
              next("/gestion-tickets");
            } else {
              next("/clientes");
            }
          };
        });
    });

    router.addRoute("interoperabilidad-view", (d) => {
      d.withPath("configuracion-general/interoperabilidad")
        .withTitle("Interoperabilidad")
        .withComponent(() => import("@/views/InteroperabilidadView.vue"))
        .withAuthorization({ authReqs: puedeGestionarInteroperabilidad });
    });

    router.addRoute("interoperabilidad-tarifas-view", (d) => {
      d.withPath("configuracion-general/interoperabilidad/tarifas")
        .withTitle("Interoperabilidad")
        .withComponent(() => import("@/views/InteroperabilidadView.vue"))
        .withAuthorization({ authReqs: puedeGestionarInteroperabilidad });
    });

    router.addRoute("interoperabilidad-inicio-view", (d) => {
      d.withPath("configuracion-general/interoperabilidad/inicio")
        .withTitle("Interoperabilidad")
        .withComponent(() => import("@/views/InteroperabilidadInicioView.vue"))
        .withAuthorization({ authReqs: puedeGestionarInteroperabilidad });
    });

    for (const [nombre, ruta] of [
      ["interoperabilidad-precios-view", "precios"],
      ["interoperabilidad-asignaciones-view", "asignaciones"],
      ["interoperabilidad-auditoria-view", "auditoria"],
    ]) {
      router.addRoute(nombre, (d) => {
        d.withPath(`configuracion-general/interoperabilidad/${ruta}`)
          .withTitle("Interoperabilidad")
          .withComponent(() => import("@/views/InteroperabilidadView.vue"))
          .withAuthorization({ authReqs: puedeGestionarInteroperabilidad });
      });
    }

    router.addRoute("admin-quely-view", (d) => {
      d.withPath("admin-quely").withComponent(() => import("@/views/Revenue/AdminQuely.vue")).withAuthorization({});
    });

    router.addRoute("admin-tareas-programadas-view", (d) => {
      d.withPath("admin-tareas-programadas")
        .withTitle("Tareas programadas")
        .withComponent(() => import("@/views/Admin/EstadoTareasProgramadasView.vue"))
        .withAuthorization({});
    });

    router.addRoute("clientes-view", (d) => {
      d.withPath("clientes")
      .withTitle("Clientes")
      .withComponent(() => import("@/views/ClientesView.vue")).withAuthorization({});
    });

    router.addRoute("usuarios-view", (d) => {
      d.withPath("usuarios")
      .withTitle("Usuarios")
      .withComponent(() => import("@/views/UsuariosView.vue")).withAuthorization({});
    });

    router.addRoute("suscripciones-view", (d) => {
      d.withPath("suscripciones")
      .withTitle("Suscripciones")
      .withComponent(() => import("@/views/SuscripcionesView.vue")).withAuthorization({});
    });

    router.addRoute("preferencias-view", (d) => {
      d.withPath("preferencias")
        .withTitle("Preferencias")
        .withComponent(() => import("@/views/PreferenciasView.vue"))
        .withAuthorization({
          authReqs: () =>
            useAuthorization().hasPermission(PermissionsApp.GestionAvanzadaActuacionesAll),
        });
    });

    router.addRoute("partners-view", (b) =>{
      b.withPath("partners")
      .withTitle("Partners")
      .withComponent(() => import("@/views/Revenue/PartnersView.vue")).withAuthorization({});
    });

    router.addRoute("activos-view", (b) =>{
      b.withPath("activos")
      .withTitle("Activos")
      .withComponent(() => import("@/views/Revenue/ActivosView.vue")).withAuthorization({});
    });

    router.addRoute("activos-sustituciones-view", (b) => {
      b.withPath("activos/sustituciones")
        .withTitle("Sustituciones")
        .withComponent(() => import("@/views/Revenue/ActivosSustitucionesView.vue"))
        .withAuthorization({})
        .configure((r) => {
          r.beforeEnter = async (_to, _from, next) => {
            const a = useAuthorization();
            const api = useApi();
            const esGmaoActivos =
              a.hasPermission(PermissionsApp.EditAllActivosGmao) ||
              a.hasPermission(PermissionsApp.EditSelfActivosGmao);
            if (esGmaoActivos || a.hasPermission(PermissionsApp.EditAllActivosRevenue)) {
              return next();
            }
            if (
              a.hasPermission(PermissionsApp.EditSelfActivosRevenue) &&
              !a.hasPermission(PermissionsApp.EditAllActivosRevenue)
            ) {
              const puede = await api.callStandardApi("getRevenueSelfPuedeVerWoSustituciones", { showLoading: false });
              if (!puede) {
                return next({ path: "/activos" });
              }
            }
            next();
          };
        });
    });

    router.addRoute("activos-dismissed-view", (b) => {
      b.withPath("activos/dismissed")
        .withTitle("Activos dismissed")
        .withComponent(() => import("@/views/Revenue/ActivosDismissedView.vue"))
        .withAuthorization({})
        .configure((r) => {
          r.beforeEnter = async (_to, _from, next) => {
            const a = useAuthorization();
            if (a.hasPermission(PermissionsApp.EditAllActivosGmao)) {
              return next();
            }
            return next({ path: "/activos" });
          };
        });
    });

    router.addRoute("activos-dashboard-view", (b) =>
      b.withPath("activos/dashboard")
        .withTitle("Dashboard activos")
        .withComponent(() => import("@/views/Revenue/DashboardActivosView.vue"))
        .withAuthorization({
          authReqs: () => puedeVerDashboardActivos(useAuthorization()),
        })
    );

    router.addRoute("informes-view", (b) =>{
      b.withPath("informes").withComponent(() => import("@/views/Revenue/TransaccionesView.vue"))
      .withTitle("Informe variable")
      .withAuthorization({});
    });

    router.addRoute("informes-fijo-view", (b) =>{
      b.withPath("informes-fijo").withComponent(() => import('@/views/Revenue/InformesFijoView.vue'))
      .withTitle("Canon fijo")
      .withAuthorization({});
    });

    router.addRoute("revenue-dashboard-view", (b) => {
      b.withPath("revenue-dashboard")
        .withTitle("Dashboard")
        .withComponent(() => import("@/views/Revenue/RevenueDashboardView.vue"))
        .withAuthorization({
          authReqs: () => isGestorRevenueAdmin(useAuthorization()),
        });
    });

    router.addRoute("revenue-total-devengado-view", (b) => {
      b.withPath("revenue-total-devengado")
        .withTitle("Total devengado")
        .withComponent(() => import("@/views/Revenue/RevenueTotalDevengadoView.vue"))
        .withAuthorization({
          authReqs: () => isGestorRevenueAdmin(useAuthorization()),
        });
    });

    router.addRoute("activo-detalle-view", d => {
      d.withPath("activo-detalle/:id?")
      .withTitle("Detalle de activos")
      .withComponent(() => import('@/views/Revenue/DetalleActivoView.vue')).withAuthorization({})
        .configure(r => {
          r.props = (route) => ({
            id: route.params.id,
          });
        })
    });

    router.addRoute("partner-detalle-view", d => {
      d.withPath("partner-detalle/:id?").withComponent(() => import('@/views/Revenue/DetallePartnerView.vue'))
      .withTitle("Detalle de partner")
      .withAuthorization({})
        .configure(r => {
          r.props = route => ({
            id: route.params.id,
            modoEdicion: route.query.modoEdicion === 'true'
          });
        })
    });

    router.addRoute("transaccion-detalle-view", d => {
      d.withPath("informe-detalle/:id?")
      .withComponent(() => import('@/views/Revenue/DetalleTransaccionView.vue'))
      .withTitle("Detalle de informes")
      .withAuthorization({})
        .configure(r => {
          r.props = true,
          r.alias = ['transaccion-detalle/:id?']
        })
    });

    router.addRoute("pds-view", (d) => {
      d.withPath("pds").withComponent(() => import("@/views/Gmao/PdsView.vue")).withAuthorization({}).withTitle("Prestador");
    });

    router.addRoute("buscador-prestadores-view", (d) => {
      d.withPath("buscador-prestadores")
      .withComponent(() => import('@/views/Gmao/BuscadorPdsView.vue'))
      .withAuthorization({
        authReqs: (userInfo) => puedeVerNavBuscadorPrestadores(useAuthorization(), userInfo),
      })
      .withTitle("Buscador de prestadores");
    });

    router.addRoute("gestion-actuaciones-view", (d) => {
      d.withPath("gestion-actuaciones").withComponent(() => import("@/views/Gmao/GestionActuacionesView.vue")).withAuthorization({
        authReqs: (userInfo) => puedeVerNavActuaciones(useAuthorization(), userInfo),
      }).withTitle("Actuaciones");
      
    });
    
    router.addRoute("gestione-wo-view", (d) => {
      d.withPath("gestion-wo")
        .withComponent(() => import("@/views/Gmao/GestionWoView.vue"))
        .withAuthorization({})
        .withTitle("Work Orders")
        .configure((r) => {
          r.beforeEnter = async (_to, _from, next) => {
            const a = useAuthorization();
            const userInfo = useAuthentication().userInfo as ExtendedUserInfo | null;
            const api = useApi();
            if (
              a.hasPermission(PermissionsApp.EditAllWo) ||
              a.hasPermission(PermissionsApp.EditSelfWo) ||
              puedeVerNavDashboardWo(a, userInfo) ||
              a.hasPermission(PermissionsApp.EditAllActivosRevenue)
            ) {
              return next();
            }
            if (
              a.hasPermission(PermissionsApp.EditSelfActivosRevenue) &&
              !a.hasPermission(PermissionsApp.EditAllActivosRevenue)
            ) {
              const puede = await api.callStandardApi("getRevenueSelfPuedeVerWoSustituciones", { showLoading: false });
              if (!puede) {
                return next({ path: "/home" });
              }
              return next();
            }
            return next({ path: "/home" });
          };
        });
    });

    router.addRoute("maestro-de-pagos-view",(d)=>{
      d.withPath("maestro-de-pagos").withComponent(() => import("@/views/Gmao/MaestroDePagosView.vue")).withAuthorization({
        authReqs: (userInfo) => {
          const a = useAuthorization();
          return a.hasPermission(PermissionsApp.EditAllWo) || a.hasPermission(PermissionsApp.EditSelfWo);
        },
      }).withTitle("Maestro de pagos")
    });

    router.addRoute("maestro-de-pagos-graficos-view",(d)=>{
      d.withPath("maestro-de-pagos-graficos").withComponent(() => import("@/views/Gmao/GraficosMaestroPagosView.vue")).withAuthorization({
        authReqs: () => useAuthorization().hasPermission(PermissionsApp.EditAllWo),
      }).withTitle("Gráficos y estadísticas")
    });

    router.addRoute("maestro-de-pagos-prestador-view",(d)=>{
      d.withPath("maestro-de-pagos-prestador").withComponent(() => import("@/views/Gmao/MaestroDePagosPrestadorView.vue")).withAuthorization({
        authReqs: (userInfo) => puedeVerNavMaestroPagos(useAuthorization(), userInfo),
      }).withTitle("Maestro de pagos prestador")
    });

    router.addRoute("maestro-de-pagos-b2c-view",(d)=>{
      d.withPath("maestro-de-pagos-b2c").withComponent(() => import("@/views/Gmao/MaestroDePagosB2cView.vue")).withAuthorization({
        authReqs: (userInfo) => puedeVerNavMaestroPagosB2c(useAuthorization(), userInfo),
      }).withTitle("Maestro de pagos B2C")
    });

    router.addRoute("maestro-de-pagos-iv-view", (d) => {
      d.withPath("maestro-de-pagos-iv").withComponent(() => import('@/views/InspeccionesVisuales/MaestroDePagosInspeccionesVisualesView.vue')).withAuthorization({
        authReqs: (userInfo) => puedeVerNavMaestroPagosInspeccionesVisuales(useAuthorization(), userInfo),
      }).withTitle("Maestro de Pagos IV");
    });

    router.addRoute("nueva-actuacion-view", (d) => {
      d.withPath("nueva-actuacion").withComponent(() => import("@/views/Gmao/NuevaActuacionView.vue")).withAuthorization({
        authReqs: (userInfo) => puedeVerNavCrearActuacion(useAuthorization(), userInfo),
      }).withTitle("Nueva actuación");
    });

    router.addRoute("detalle-actuacion-view", d => {
      d.withPath("detalle-actuacion/:id?").withComponent(() => import('@/views/Gmao/DetalleActuacionView.vue')).withAuthorization({}).withTitle("Detalle actuación")
        .configure(r => {
          r.props = route => ({
            id: route.params.id,
            modoEdicion: route.query.modoEdicion === 'true'
          });
        })
    });

    router.addRoute("informe-tecnico-actuacion-view", (d) => {
      d.withPath("informe-tecnico-actuacion/:id").withComponent(() => import('@/views/Gmao/InformeTecnicoActuacionView.vue')).withAuthorization({}).withTitle("Informe técnico actuación")      
        .configure(r => {
          r.props = route => ({
            id: route.params.id
          });
        })
    });

    router.addRoute("informe-tecnico-wo-view", (d) => {
      d.withPath("informe-tecnico-wo/:id/:idActuacion")
      .withComponent(() => import('@/views/Gmao/InformeTecnicoWoView.vue'))
      .withTitle("Informe técnico work order")
      .withAuthorization({})      
        .configure(r => {
          r.props = route => ({
            idWo: route.params.id,
            idActuacion: route.params.idActuacion
          });
        })
    });

    router.addRoute("detalle-wo-view", d => {
      d.withPath("detalle-wo/:id?/:idActuacion?").withTitle("Detalle de work order")
        .withComponent(() => import('@/views/Gmao/DetalleWoView.vue'))
        .withAuthorization({})
        .configure(r => {
          r.props = true;
          r.beforeEnter = (to, from, next) => {
            const a = useAuthorization();
            const puedeWoGmao = a.hasPermission(PermissionsApp.EditAllWo) || a.hasPermission(PermissionsApp.EditSelfWo);
            const soloRevenueActivos =
              (a.hasPermission(PermissionsApp.EditAllActivosRevenue) || a.hasPermission(PermissionsApp.EditSelfActivosRevenue)) &&
              !puedeWoGmao;
            if (soloRevenueActivos) {
              return next({ path: "/gestion-wo" });
            }
            next();
          };
        });
    });

    router.addRoute("equipo-medida-view", (d) => {
      d.withPath("equipo-medida").withComponent(() => import("@/views/Gmao/EquipoMedidaView.vue")).withAuthorization({
        authReqs: (userInfo) => puedeVerNavEquipoMedida(useAuthorization(), userInfo),
      }).withTitle("Equipos de medida");
    });

    router.addRoute("gestion-tickets-view", (d) => {
      d.withPath("gestion-tickets")
        .withComponent(() => import("@/views/GestionTickets/GestionTicketView.vue"))
        .withAuthorization({
          authReqs: (userInfo) => puedeVerNavTicketing(useAuthorization(), userInfo),
        })
        .withTitle("Tickets");
    
    });

    router.addRoute("detalle-ticket-view", (d) => {
      d.withPath("detalle-ticket/:id")
        .withComponent(() => import("@/views/GestionTickets/DetalleTicketView.vue"))
        .withAuthorization({
          authReqs: (userInfo) => puedeVerNavTicketing(useAuthorization(), userInfo),
        })
        .withTitle("Detalle ticket")
        .configure(r => {
          r.props = true;
        });
    });

    router.addRoute("recuento-activos-view", (d) => {
      d.withPath("recuento-activos").withComponent(() => import("@/views/Gmao/RecuentoActivosView.vue")).withAuthorization({}).withTitle("Recuento de activos");
    });

    router.addRoute("dashboard-wo-view", (d) => {
      d.withPath("dashboard-wo").withComponent(() => import("@/views/Gmao/DashboardWoView.vue")).withAuthorization({
        authReqs: (userInfo) => puedeVerNavDashboardWo(useAuthorization(), userInfo),
      }).withTitle("Dashboard Work Orders");
    });

    router.addRoute("dashboard-kpi-gmao-view", (d) => {
      d.withPath("dashboard-kpi-gmao")
        .withComponent(() => import("@/views/Gmao/DashboardKpiGmaoView.vue"))
        .withAuthorization({
          authReqs: () => puedeVerNavDashboardKpiGmao(useAuthorization()),
        })
        .withTitle("KPI GMAO");
    });

    router.addRoute("gestion-campanas-view", (d) => {
      d.withPath("gestion-campanas").withComponent(() => import('@/views/Gmao/GestionCampanasView.vue')).withAuthorization({
        authReqs: (userInfo) => puedeVerCampanas(useAuthorization(), userInfo),
      }).withTitle("Campañas");
    });

    router.addRoute("detalle-campana-view", d => {
      d.withPath("detalle-campana/:id?").withComponent(() => import('@/views/Gmao/DetalleCampanaView.vue')).withAuthorization({})
      .withTitle("Detalle campaña")
        .configure(r => {
          r.props = route => ({
            id: route.params.id,
          });
        })
    });

    router.addRoute("dashboard-campanas-view", (d) => {
      d.withPath("dashboard-campanas").withComponent(() => import("@/views/Gmao/DashboardCampanasView.vue")).withAuthorization({
        authReqs: (userInfo) => puedeVerCampanas(useAuthorization(), userInfo),
      }).withTitle("Dashboard Campañas");
    });

    router.addRoute("inspecciones-visuales-view", (d) => {
      d.withPath("inspecciones-visuales")
        .withComponent(() => import("@/views/InspeccionesVisuales/InspeccionesVisualesView.vue"))
        .withAuthorization({
          authReqs: (userInfo) => puedeVerNavInspeccionesVisuales(useAuthorization(), userInfo),
        })
        .withTitle("Inspecciones visuales");
    });

    router.addRoute("inspecciones-visuales-detalle-view", (d) => {
      d.withPath("inspecciones-visuales/:id")
        .withComponent(() => import("@/views/InspeccionesVisuales/DetalleInspeccionVisualView.vue"))
        .withAuthorization({
          authReqs: (userInfo) => puedeAccederRutasInspeccionesVisuales(useAuthorization(), userInfo),
        })
        .withTitle("inspecciones_visuales.detalle.titulo")
        .configure((r) => {
          r.props = true;
        });
    });

    router.addRoute("compliance-inspeccion-visual-view", (d) => {
      d.withPath("inspeccion-visual/:id/compliance")
        .withComponent(() => import("@/views/InspeccionesVisuales/ComplianceInspeccionVisualView.vue"))
        .withAuthorization({ authReqs: () => useAuthorization().hasPermission(PermissionsApp.EditarComplianceIv) })
        .withTitle("inspecciones_visuales.compliance.titulo_ruta")
        .configure((r) => { r.props = true; });
    });
    router.addRoute("informe-tecnico-inspeccion-visual-view", (d) => {
      d.withPath("informe-tecnico-inspeccion-visual/:id")
        .withComponent(() => import("@/views/InspeccionesVisuales/InformeTecnicoInspeccionVisualView.vue"))
        .withAuthorization({
          authReqs: (userInfo) => puedeAccederRutasInspeccionesVisuales(useAuthorization(), userInfo),
        })
        .withTitle("inspecciones_visuales.informe.titulo_ruta")
        .configure((r) => {
          r.props = (route) => ({
            id: route.params.id,
          });
        });
    });

    router.addRoute("informe-tecnico-inspeccion-visual-cargador-view", (d) => {
      d.withPath("informe-tecnico-inspeccion-visual/:id/cargador/:idCargador")
        .withComponent(() => import("@/views/InspeccionesVisuales/InformeTecnicoInspeccionVisualView.vue"))
        .withAuthorization({
          authReqs: (userInfo) => puedeAccederRutasInspeccionesVisuales(useAuthorization(), userInfo),
        })
        .withTitle("inspecciones_visuales.informe.titulo_ruta")
        .configure((r) => {
          r.props = (route) => ({
            id: route.params.id,
            idCargador: route.params.idCargador,
          });
        });
    });

    router.addRoute("informe-tecnico-inspeccion-visual-resumen-view", (d) => {
      d.withPath("informe-tecnico-inspeccion-visual/:id/resumen")
        .withComponent(() => import("@/views/InspeccionesVisuales/InformeTecnicoInspeccionVisualResumenView.vue"))
        .withAuthorization({
          authReqs: (userInfo) => puedeAccederRutasInspeccionesVisuales(useAuthorization(), userInfo),
        })
        .withTitle("inspecciones_visuales.resumen.titulo_ruta")
        .configure((r) => {
          r.props = (route) => ({
            id: route.params.id,
          });
        });
    });

    router.addRoute("oca-ocas-view", (d) => {
      d.withPath("oca/ocas")
        .withComponent(() => import("@/views/oca/OcasView.vue"))
        .withAuthorization({
          authReqs: (userInfo) => puedeVerNavOcas(useAuthorization(), userInfo),
        })
        .withTitle("oca.ocas.titulo");
    });

    router.addRoute("oca-dashboard-view", (d) => {
      d.withPath("oca/dashboard")
        .withComponent(() => import("@/views/oca/DashboardOcasView.vue"))
        .withAuthorization({
          authReqs: () => tienePermisoGestionOcasCatalogo(useAuthorization()),
        })
        .withTitle("oca.menu.dashboard");
    });

    router.addRoute("oca-seguimiento-view", (d) => {
      d.withPath("oca/seguimiento")
        .withComponent(() => import("@/views/oca/SeguimientoOcasView.vue"))
        .withAuthorization({
          authReqs: (userInfo) => puedeVerNavSeguimientoOcas(useAuthorization(), userInfo),
        })
        .withTitle("oca.menu.seguimiento");
    });

    router.addRoute("oca-ubicaciones-view", (d) => {
      d.withPath("oca/ubicaciones")
        .withComponent(() => import("@/views/oca/UbicacionesView.vue"))
        .withAuthorization({
          authReqs: () => tienePermisoGestionOcasCatalogo(useAuthorization()),
        })
        .withTitle("oca.ubicaciones.titulo");
    });

    router.addRoute("oca-centros-transformacion-view", (d) => {
      d.withPath("oca/centros-transformacion")
        .withComponent(() => import("@/views/oca/CentrosTransformacionView.vue"))
        .withAuthorization({
          authReqs: () => tienePermisoGestionOcasCatalogo(useAuthorization()),
        })
        .withTitle("oca.centros_transformacion.titulo");
    });

    router.addRoute("oca-detalle-ubicacion-view", (d) => {
      d.withPath("oca/detalle-ubicacion/:id?")
        .withComponent(() => import("@/views/oca/DetalleOcaUbicacionView.vue"))
        .withAuthorization({
          authReqs: (userInfo) => puedeVerNavOcas(useAuthorization(), userInfo),
        })
        .withTitle("oca.ocas.detalle.titulo_ubicacion")
        .configure((r) => {
          r.props = (route) => ({
            id: route.params.id,
          });
        });
    });

    router.addRoute("oca-detalle-centro-transformacion-view", (d) => {
      d.withPath("oca/detalle-centro-transformacion/:id?")
        .withComponent(() => import("@/views/oca/DetalleOcaCentroTransformacionView.vue"))
        .withAuthorization({
          authReqs: (userInfo) => puedeVerNavOcas(useAuthorization(), userInfo),
        })
        .withTitle("oca.ocas.detalle.titulo_centro_transformacion")
        .configure((r) => {
          r.props = (route) => ({
            id: route.params.id,
          });
        });
    });

    router.addRoute("oca-ubicacion-detalle-view", (d) => {
      d.withPath("oca/ubicaciones-detalle/:id?")
        .withComponent(() => import("@/views/oca/DetalleUbicacionView.vue"))
        .withAuthorization({
          authReqs: () => tienePermisoGestionOcasCatalogo(useAuthorization()),
        })
        .withTitle("oca.ubicaciones.detalle.titulo")
        .configure((r) => {
          r.props = (route) => ({
            id: route.params.id,
          });
        });
    });

    router.addRoute("oca-centro-transformacion-detalle-view", (d) => {
      d.withPath("oca/centros-transformacion-detalle/:id?")
        .withComponent(() => import("@/views/oca/DetalleCentroTransformacionView.vue"))
        .withAuthorization({
          authReqs: () => tienePermisoGestionOcasCatalogo(useAuthorization()),
        })
        .withTitle("oca.centros_transformacion.detalle.titulo")
        .configure((r) => {
          r.props = (route) => ({
            id: route.params.id,
          });
        });
    });

  });
}
